import { useEffect, useMemo, useRef } from 'react';
import type { Content } from '@playnets/sim';
import { useGame } from '../game/useGame';
import { useUi } from '../store';
import Hud from '../components/Hud';
import BuildSheet from '../components/BuildSheet';
import WaveStart from '../components/WaveStart';
import BuildBar from '../components/BuildBar';
import Coach from '../components/Coach';
import PauseMenu from '../components/PauseMenu';

export default function Battle({
  planetId,
  levelId,
  evolutions,
  unlockedStructures,
  seed,
  content,
  onFinish,
  onRestartLevel,
  onExit,
}: {
  planetId: string;
  levelId: string;
  evolutions: readonly string[];
  unlockedStructures: readonly string[];
  seed: number;
  content: Content;
  onFinish: (won: boolean, damage: number, coreMax: number) => void;
  onRestartLevel: () => void;
  onExit: () => void;
}): JSX.Element {
  const holderRef = useRef<HTMLDivElement | null>(null);
  const overlayRef = useRef<HTMLDivElement | null>(null);

  const opts = useMemo(
    () => ({ planetId, levelId, evolutions, unlockedStructures, seed }),
    [planetId, levelId, evolutions, unlockedStructures, seed],
  );
  const game = useGame(holderRef, overlayRef, opts, onFinish);

  // Desktop keyboard shortcuts (§9). Nothing here is required to play.
  useEffect(() => {
    if (!game) return;
    const onKey = (ev: KeyboardEvent) => {
      const ui = useUi.getState();
      switch (ev.key) {
        case ' ':
          ev.preventDefault();
          game.input.push({ kind: 'ultimate', bearing: -Math.PI / 2 });
          break;
        case 'f':
        case 'F':
          ui.toggleSpeed();
          break;
        case 'Escape':
          ui.setPaused(!ui.paused);
          break;
        default:
          break;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [game]);

  return (
    <>
      <div className="plate-canvas" ref={holderRef} />
      <div className="overlay" ref={overlayRef} />
      <Hud game={game} />
      <BuildBar game={game} />
      <WaveStart game={game} />
      <Coach game={game} />
      <PauseMenu game={game} onRestartLevel={onRestartLevel} onExit={onExit} />
      <BuildSheet game={game} />
    </>
  );
}
